/**
 * @contract
 *   Назначение:  Виджет "Вы владелец этого ЧОПа?" — захват лидов для Claim Profile
 *                Если пользователь авторизован — ссылка на кабинет
 *   Компонент:  ClaimProfileWidget (client-side)
 *   Автор:      AI Agent (Фаза 46, updated Phase 3)
 *   Дата:       2026-06-26
 *   CHANGE:     Добавлена проверка авторизации через usePortalSession
 */

"use client";

import React, { useState } from "react";
import { Shield, Sparkles, CheckCircle } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import Link from "next/link";
import { usePortalSession } from "./portal-session-provider";

interface ClaimProfileWidgetProps {
  companyInn: string | null;
  companyName: string;
  companySlug?: string;
}

export function ClaimProfileWidget({
  companyInn,
  companyName,
  companySlug,
}: ClaimProfileWidgetProps) {
  const { user, isLoading } = usePortalSession();
  const [step, setStep] = useState<"idle" | "form" | "done">("idle");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await fetch("/api/seo/claim-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, inn: companyInn, company_name: companyName }),
      });
      setStep("done");
    } catch {
      window.location.href = `/register?inn=${companyInn ?? ""}&source=claim`;
    } finally {
      setLoading(false);
    }
  };

  // Если пользователь авторизован — показываем ссылку на кабинет
  if (!isLoading && user) {
    return (
      <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-accent-muted))] p-5 rounded-none">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-[hsl(var(--portal-accent))]" />
          <h3 className="font-bold text-[hsl(var(--portal-text))] uppercase tracking-wide text-xs">
            Вы владелец профиля
          </h3>
        </div>
        <p className="text-sm text-[hsl(var(--portal-text-muted))] mb-4 font-sans">
          Управляйте репутацией, отвечайте на отзывы и редактируйте информацию
          о компании <strong className="text-[hsl(var(--portal-text))]">{companyName}</strong>.
        </p>
        <Link href={"/cabinet" + (companySlug ? "?company=" + companySlug : "")}>
          <Button
            variant="outline"
            className="w-full justify-center bg-transparent border-[hsl(var(--portal-accent-muted))] hover:bg-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent-on))] rounded-none transition-all font-bold text-xs uppercase tracking-wide"
          >
            Управлять профилем
          </Button>
        </Link>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-accent-muted))] p-5 rounded-none">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-[hsl(var(--portal-accent))]" />
          <h3 className="font-bold text-[hsl(var(--portal-text))] uppercase tracking-wide text-xs">
            Заявка отправлена
          </h3>
        </div>
        <p className="text-sm text-[hsl(var(--portal-text-muted))] font-sans">
          Мы свяжемся с вами по адресу <strong className="text-[hsl(var(--portal-text))]">{email}</strong>.
          Доступ к управлению профилем будет открыт после верификации.
        </p>
      </div>
    );
  }

  if (step === "form") {
    return (
      <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border-hover))] p-5 rounded-none">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[hsl(var(--portal-warn))]" />
          <h3 className="font-bold text-[hsl(var(--portal-text))] uppercase tracking-wide text-xs">
            Подтвердите владение
          </h3>
        </div>
        <p className="text-sm text-[hsl(var(--portal-text-muted))] mb-4 font-sans">
          Введите корпоративную эл. почту, чтобы получить доступ к профилю{" "}
          <strong className="text-[hsl(var(--portal-text))]">{companyName}</strong>.
        </p>
        <form onSubmit={handleClaim} className="space-y-3">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ваша@почта.рф"
            required
            className="bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] rounded-none"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] rounded-none font-bold tracking-wide text-xs uppercase"
          >
            {loading ? "Отправляем..." : "Получить доступ"}
          </Button>
          <p className="text-[10px] text-[hsl(var(--portal-text-dim))] text-center font-sans">
            Или{" "}
            <Link
              href="/login?redirect=/claim"
              className="underline hover:text-[hsl(var(--portal-text))] transition-colors"
            >
              войдите в аккаунт
            </Link>
            {" / "}
            <Link
              href={`/register?inn=${companyInn ?? ""}&source=claim`}
              className="underline hover:text-[hsl(var(--portal-text))] transition-colors"
            >
              зарегистрируйтесь
            </Link>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] p-5 rounded-none group hover:border-[hsl(var(--portal-border-hover))] transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-[hsl(var(--portal-text))]" />
        <h3 className="font-bold text-[hsl(var(--portal-text))] uppercase tracking-wide text-xs">
          Вы владелец бизнеса?
        </h3>
      </div>
      <p className="text-sm text-[hsl(var(--portal-text-muted))] mb-4 font-sans">
        Управляйте репутацией, отвечайте на отзывы и привлекайте клиентов
        через OXPA.PRO — бесплатно.
      </p>
      <div className="space-y-2 text-[11px] text-[hsl(var(--portal-text-muted))] font-sans mb-4">
        <p>✓ Добавьте логотип и описание компании</p>
        <p>✓ Отвечайте на отзывы сотрудников</p>
        <p>✓ Публикуйте вакансии (охранники)</p>
        <p>✓ Получайте лиды от заказчиков охраны</p>
      </div>
      <Button
        onClick={() => setStep("form")}
        variant="outline"
        className="w-full justify-center bg-transparent border-[hsl(var(--portal-border-hover))] hover:bg-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent-on))] rounded-none transition-all font-bold text-xs uppercase tracking-wide"
      >
        Управлять профилем
      </Button>
    </div>
  );
}